import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API from "../../api/api";
import Navbar from "../../components/Navbar";

const VerifyCertificate = () => {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if logged in user is admin/secretary
    const role = sessionStorage.getItem("role") || localStorage.getItem("role");
    if (role === "admin" || role === "secretary" || role === "district") {
      setIsAdmin(true);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await API.get(`/verify/certificate/${code}`);
        setData(res);
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || "Invalid verification code or certificate not found.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code]);

  return (
    <>
      <Navbar />
      <div className="flex flex-col items-center mt-12 sm:mt-20 text-center px-4 mobile-page-pad pb-20">
        {loading && (
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="text-blue-600 font-medium">Verifying Certificate...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 w-full max-w-md shadow-sm">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl text-red-600">✕</span>
            </div>
            <h2 className="text-lg font-bold text-red-800 mb-1">Verification Failed</h2>
            <p className="text-red-600 font-medium text-sm">{error}</p>
            <p className="text-gray-500 text-xs mt-4">The verification code <strong>{code}</strong> is not recognized by our system. Please check the spelling or ensure the certificate is authentic.</p>
          </div>
        )}

        {data && (
          <div className="bg-white shadow-xl rounded-2xl p-6 sm:p-8 w-full max-w-lg border border-gray-100 relative overflow-hidden transition-all duration-300 hover:shadow-2xl">
            {/* Top decorative gradient bar */}
            <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${data.revoked ? 'from-red-500 to-rose-600' : 'from-indigo-600 to-blue-500'}`}></div>
            
            {data.revoked ? (
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5 border border-red-100">
                <span className="text-3xl text-red-500">✕</span>
              </div>
            ) : (
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5 border border-emerald-100">
                <span className="text-3xl text-emerald-500">✓</span>
              </div>
            )}

            {data.revoked ? (
              <>
                <h2 className="text-xl sm:text-2xl text-red-800 font-extrabold mb-2 tracking-tight">Revoked Certificate</h2>
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm font-semibold rounded-lg p-3 mb-5">
                  This certificate has been revoked.
                  {data.revoked_reason && <p className="text-xs font-normal mt-1 text-red-600">Reason: {data.revoked_reason}</p>}
                  {data.revoked_at && <p className="text-[10px] font-normal text-red-500">Revocation Date: {new Date(data.revoked_at).toLocaleDateString()}</p>}
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl sm:text-2xl text-emerald-800 font-extrabold mb-1 tracking-tight">Certificate Verified</h2>
                <p className="text-emerald-600 text-xs font-semibold uppercase tracking-wider mb-5">Authentic & Valid</p>
              </>
            )}

            {/* Admin Badge */}
            {isAdmin && !data.revoked && (
              <div className="bg-blue-50 border border-blue-200 text-blue-800 text-xs font-bold rounded-lg px-4 py-2.5 mb-5 inline-flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.9L10 1.154l7.834 3.746a1 1 0 01.616.92v5.334c0 4.882-3.267 9.136-7.75 10.706a1 1 0 01-.7 0C5.433 20.29 2.166 16.036 2.166 11.154V5.82a1 1 0 01.616-.92zM10 13.882a1 1 0 100-2 1 1 0 000 2zm1-5.118a1 1 0 10-2 0v2a1 1 0 102 0v-2z" clipRule="evenodd" />
                </svg>
                Official Verification: Match Confirmed with Database
              </div>
            )}

            <div className="space-y-4 text-left border-t border-gray-100 pt-4">
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Player Name</label>
                <p className="font-extrabold text-gray-800 text-lg mt-0.5">{data.player_name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Player ID</label>
                  <p className="font-mono font-semibold text-gray-700 mt-0.5">{data.player_reg_id || "N/A"}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">District</label>
                  <p className="font-semibold text-gray-700 mt-0.5">{data.player_district || "Andhra Pradesh"}</p>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Event Title</label>
                <p className="font-semibold text-gray-700 text-sm mt-0.5">{data.event_title}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Game / Sport</label>
                  <p className="font-semibold text-gray-700 mt-0.5">{data.game_name}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Certificate Type</label>
                  <p className={`font-bold mt-0.5 ${data.medal_type === 'Participation' ? 'text-blue-600' : 'text-amber-600'}`}>
                    {data.medal_type === 'Participation' ? 'Participation' : `${data.medal_type} Medal`}
                  </p>
                </div>
              </div>

              <div className="border-t border-dashed border-gray-100 pt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Certificate Number</label>
                  <p className="font-mono font-bold text-gray-800 mt-0.5">{data.serial_no}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Verification Code</label>
                  <p className="font-mono font-bold text-gray-800 mt-0.5">{code}</p>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Issue Date</label>
                <p className="text-gray-600 text-xs mt-0.5">{new Date(data.issued_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default VerifyCertificate;
